import { supabase } from "../supabase/client";
import { GACHA_REWARDS, type RewardType } from "../surprise/surpriseRepository";
import { isCloudChildId } from "./starWallet";

export interface InventoryEntry {
  rewardId: string;
  itemType: Exclude<RewardType, "stars">;
  quantity: number;
  acquiredAt: string;
  acquiredFrom: string;
  metadata: Record<string, unknown>;
}

export interface RoomPlacement {
  id: string;
  rewardId: string;
  x: number;
  y: number;
  scale: number;
  layer: number;
}

export interface PlayerCollection {
  inventory: InventoryEntry[];
  placements: RoomPlacement[];
  equipped: Record<string, string>;
  source: "cloud" | "local";
}

const LEGACY_ROOM_KEY = "lusky-room-v2";
const DEFAULT_OWNED = ["star-cushion"];
const legacyTypeMap: Record<string, string> = { star: "star-cushion", plant: "moon-plant", lamp: "moon-lamp", toy: "cloud-bunny" };

function parse<T>(value: string | null, fallback: T): T {
  try { return value ? JSON.parse(value) as T : fallback; } catch { return fallback; }
}

function collectionKey(childId: string | null) {
  return `lusky:collection:${childId || "local-child"}:v3`;
}

function inventoryKey(childId: string | null) {
  return `lusky:inventory:${childId || "local-child"}:v1`;
}

function defaultInventory(childId: string | null): InventoryEntry[] {
  const ids = new Set([...DEFAULT_OWNED, ...parse<string[]>(localStorage.getItem(inventoryKey(childId)), [])]);
  const legacy = parse<{ clothes?: string[]; toys?: string[]; inventory?: string[] }>(localStorage.getItem(LEGACY_ROOM_KEY), {});
  [...(legacy.clothes ?? []), ...(legacy.toys ?? []), ...(legacy.inventory ?? [])].forEach((id) => ids.add(id));
  return [...ids].flatMap((rewardId) => {
    const reward = GACHA_REWARDS.find((entry) => entry.id === rewardId);
    if (!reward || reward.type === "stars") return [];
    return [{ rewardId, itemType: reward.type, quantity: 1, acquiredAt: new Date(0).toISOString(), acquiredFrom: "legacy_migration", metadata: {} } satisfies InventoryEntry];
  });
}

function readLocalCollection(childId: string | null): PlayerCollection {
  const saved = parse<Partial<PlayerCollection>>(localStorage.getItem(collectionKey(childId)), {});
  const legacy = parse<{ placed?: Array<{ id: string; type: string; x: number; y: number }> }>(localStorage.getItem(LEGACY_ROOM_KEY), {});
  const placements = Array.isArray(saved.placements) ? saved.placements : (legacy.placed ?? []).flatMap((item) => {
    const rewardId = legacyTypeMap[item.type];
    return rewardId ? [{ id: item.id, rewardId, x: item.x, y: item.y, scale: 1, layer: 1 }] : [];
  });
  const base = defaultInventory(childId);
  const merged = new Map(base.map((item) => [item.rewardId, item]));
  for (const item of saved.inventory ?? []) merged.set(item.rewardId, item);
  return { inventory: [...merged.values()], placements, equipped: saved.equipped ?? {}, source: "local" };
}

function writeLocalCollection(childId: string | null, value: PlayerCollection) {
  localStorage.setItem(collectionKey(childId), JSON.stringify({ ...value, source: "local" }));
}

export async function loadPlayerCollection(childId: string | null): Promise<PlayerCollection> {
  const local = readLocalCollection(childId);
  if (!supabase || !isCloudChildId(childId)) return local;
  const [inventoryResult, placementResult, equipmentResult] = await Promise.all([
    supabase.from("child_inventory").select("reward_id,item_type,quantity,acquired_at,acquired_from,metadata").eq("child_id", childId),
    supabase.from("child_room_placements").select("id,reward_id,x_percent,y_percent,scale,layer").eq("child_id", childId).order("layer"),
    supabase.from("child_equipment").select("slot,reward_id").eq("child_id", childId)
  ]);
  if (inventoryResult.error) return local;
  const inventory = (inventoryResult.data ?? []).map((row) => ({
    rewardId: String(row.reward_id),
    itemType: row.item_type as InventoryEntry["itemType"],
    quantity: Number(row.quantity),
    acquiredAt: String(row.acquired_at),
    acquiredFrom: String(row.acquired_from),
    metadata: (row.metadata ?? {}) as Record<string, unknown>
  }));
  const placements = placementResult.error ? local.placements : (placementResult.data ?? []).map((row) => ({
    id: String(row.id), rewardId: String(row.reward_id), x: Number(row.x_percent), y: Number(row.y_percent), scale: Number(row.scale), layer: Number(row.layer)
  }));
  const equipped = equipmentResult.error ? local.equipped : Object.fromEntries((equipmentResult.data ?? []).map((row) => [String(row.slot), String(row.reward_id)]));
  const collection: PlayerCollection = { inventory: inventory.length ? inventory : local.inventory, placements, equipped, source: "cloud" };
  writeLocalCollection(childId, collection);
  return collection;
}

export async function saveRoomPlacements(childId: string | null, collection: PlayerCollection): Promise<void> {
  writeLocalCollection(childId, collection);
  if (!supabase || !isCloudChildId(childId)) return;
  const rows = collection.placements.map((item) => ({
    id: item.id, child_id: childId, reward_id: item.rewardId, x_percent: item.x, y_percent: item.y, scale: item.scale, layer: item.layer
  }));
  const keep = rows.map((row) => row.id);
  if (rows.length) await supabase.from("child_room_placements").upsert(rows, { onConflict: "id" });
  let cleanup = supabase.from("child_room_placements").delete().eq("child_id", childId);
  if (keep.length) cleanup = cleanup.not("id", "in", `(${keep.join(",")})`);
  await cleanup;
}

export async function equipInventoryItem(childId: string | null, collection: PlayerCollection, slot: string, rewardId: string): Promise<PlayerCollection> {
  if (!collection.inventory.some((item) => item.rewardId === rewardId)) throw new Error("这件物品还没有收入收藏。 ");
  const next = { ...collection, equipped: { ...collection.equipped, [slot]: rewardId } };
  writeLocalCollection(childId, next);
  if (supabase && isCloudChildId(childId)) {
    await supabase.from("child_equipment").upsert({ child_id: childId, slot, reward_id: rewardId, equipped_at: new Date().toISOString() }, { onConflict: "child_id,slot" });
  }
  return next;
}

export async function spendStars(childId: string | null, currentBalance: number, amount: number, purpose: string, referenceId = crypto.randomUUID()) {
  const cost = Math.max(1, Math.trunc(amount));
  if (currentBalance < cost) throw new Error("星光还不够，完成任务或探索可以继续收集。 ");
  if (supabase && isCloudChildId(childId)) {
    const { data, error } = await supabase.rpc("spend_child_stars", { p_child_id: childId, p_amount: cost, p_purpose: purpose, p_reference_id: referenceId });
    if (!error && data?.[0]) return Number(data[0].total_stars);
  }
  const key = `lusky:star-spends:${childId || "local-child"}:v1`;
  const rows = parse<Array<{ id: string; amount: number; purpose: string; spentAt: string }>>(localStorage.getItem(key), []);
  if (!rows.some((row) => row.id === referenceId)) rows.push({ id: referenceId, amount: cost, purpose, spentAt: new Date().toISOString() });
  localStorage.setItem(key, JSON.stringify(rows));
  const nextBalance = currentBalance - cost;
  localStorage.setItem("lusky:star-total", String(nextBalance));
  return nextBalance;
}

export async function purchaseCatalogItem(childId: string | null, collection: PlayerCollection, currentBalance: number, rewardId: string, referenceId = crypto.randomUUID()) {
  const reward = rewardDefinition(rewardId);
  if (!reward || reward.type === "stars") throw new Error("这件物品暂时不能兑换。 ");
  if (collection.inventory.some((item) => item.rewardId === rewardId)) return { collection, totalStars: currentBalance };
  const cost = reward.rarity === "magic" ? 58 : reward.rarity === "rare" ? 28 : 12;
  if (supabase && isCloudChildId(childId)) {
    const { data, error } = await supabase.rpc("purchase_catalog_item", { p_child_id: childId, p_reward_id: rewardId, p_cost: cost, p_reference_id: referenceId });
    if (!error && data?.[0]) {
      const next = await loadPlayerCollection(childId);
      return { collection: next, totalStars: Number(data[0].total_stars) };
    }
  }
  const totalStars = await spendStars(childId, currentBalance, cost, `catalog_purchase:${rewardId}`, referenceId);
  const next: PlayerCollection = {
    ...collection,
    inventory: [...collection.inventory, { rewardId, itemType: reward.type, quantity: 1, acquiredAt: new Date().toISOString(), acquiredFrom: "star_shop", metadata: {} }]
  };
  writeLocalCollection(childId, next);
  return { collection: next, totalStars };
}
export function rewardDefinition(rewardId: string) {
  return GACHA_REWARDS.find((reward) => reward.id === rewardId) ?? null;
}